<template>
  <q-badge
    class="badge-standard"
    :class="classes"
  >
    <q-icon
      v-if="startIcon"
      :name="startIcon"
    />
    <template
      v-for="(slot, slotName) in $slots"
      #[slotName]="scope"
      :key="slotName"
    >
      <slot
        :name="slotName"
        v-bind="scope || {}"
      />
    </template>
    <q-icon
      v-if="endIcon"
      :name="endIcon"
    />
  </q-badge>
</template>

<script setup>
import { computed } from 'vue'
import { cx } from '@/helpers'

defineOptions({ name: 'Badge' })
const props = defineProps({
  color: {
    type: String,
    default: 'primary',
    validator: v => [
      'primary',
      'secondary',
      'info',
      'positive',
      'negative',
      'warning',
      'accent',
      'invert',
      'disabled'
    ].includes(v)
  },
  variant: { type: String, default: 'fade', validator: v => ['solid', 'outline', 'fade', 'soft', 'dashed'].includes(v) },
  shape: { type: String, default: 'rounded', validator: v => ['square', 'rounded'].includes(v) },
  startIcon: { type: String, default: null },
  endIcon: { type: String, default: null },
  isIcon: { type: Boolean, default: false }
})

const classes = computed(() => cx(
  `variant-${props.variant}`,
  `shape-${props.shape}`,
  `color-${props.color}`
))
</script>

<style scoped>
@reference "#app-styles.css";

.badge-standard {
  @apply text-[12px] leading-3 tracking-wide py-0.5 transition-basic;

  &:deep(.q-icon) { @apply text-[12px] leading-3 }

  &.variant-solid {
    &.color-primary   { @apply bg-primary-mid text-white; }
    &.color-secondary { @apply bg-secondary-mid text-white; }
    &.color-info      { @apply bg-info-mid text-white; }
    &.color-positive  { @apply bg-positive-mid text-white; }
    &.color-negative  { @apply bg-negative-mid text-white; }
    &.color-warning   { @apply bg-warning-mid text-white; }
    &.color-accent    { @apply bg-accent-mid text-white; }
    &.color-invert    { @apply layer-invert-base ink-invert-strong; }
    &.color-disabled  { @apply bg-secondary-fainter ink-disabled; }
  }
  &.variant-outline {
    @apply py-px border;

    &.color-primary   { @apply bg-primary-mid/15 text-primary-mid border-primary-mid/60; }
    &.color-secondary { @apply bg-secondary-mid/15 text-secondary-mid border-secondary-mid/60; }
    &.color-info      { @apply bg-info-mid/15 text-info-mid border-info-mid/60; }
    &.color-positive  { @apply bg-positive-mid/15 text-positive-mid border-positive-mid/60; }
    &.color-negative  { @apply bg-negative-mid/15 text-negative-mid border-negative-mid/60; }
    &.color-warning   { @apply bg-warning-mid/15 text-warning-mid border-warning-mid/60; }
    &.color-accent    { @apply bg-accent-mid/15 text-accent-mid border-accent-mid/60; }
    &.color-invert    { @apply bg-layer-invert-base/15 ink-invert-strong border-edge-invert-base/60; }
    &.color-disabled  { @apply bg-secondary-fainter/15 ink-disabled edge-secondary-fainter; }
  }
  &.variant-faint {
    &.color-primary   { @apply bg-primary-fainter text-primary-deeper; }
    &.color-secondary { @apply bg-secondary-fainter text-secondary-deeper; }
    &.color-info      { @apply bg-info-fainter text-info-deeper; }
    &.color-positive  { @apply bg-positive-fainter text-positive-deeper; }
    &.color-negative  { @apply bg-negative-fainter text-negative-deeper; }
    &.color-warning   { @apply bg-warning-fainter text-warning-deeper; }
    &.color-accent    { @apply bg-accent-fainter text-accent-deeper; }
    &.color-invert    { @apply bg-secondary-fainter ink-strong; }
    &.color-disabled  { @apply bg-secondary-fainter/60 ink-disabled; }
  }
  &.variant-fade {
    &.color-primary   { @apply bg-primary-mid/10 text-primary-deep }
    &.color-secondary { @apply bg-secondary-mid/10 text-secondary-deep; }
    &.color-info      { @apply bg-info-mid/10 text-info-deep; }
    &.color-positive  { @apply bg-positive-mid/10 text-positive-deep; }
    &.color-negative  { @apply bg-negative-mid/10 text-negative-deep; }
    &.color-warning   { @apply bg-warning-mid/10 text-warning-deep; }
    &.color-accent    { @apply bg-accent-mid/10 text-accent-deep; }
    &.color-invert    { @apply bg-layer-invert-base/10 ink-strong; }
    &.color-disabled  { @apply bg-secondary/10 ink-disabled; }
  }

  &.variant-dashed {
    @apply bg-transparent py-px border border-dashed;

    &.color-primary   { @apply text-primary-mid edge-primary-mid; }
    &.color-secondary { @apply text-secondary-mid edge-secondary-mid; }
    &.color-info      { @apply text-info-mid edge-info-mid; }
    &.color-positive  { @apply text-positive-mid edge-positive-mid; }
    &.color-negative  { @apply text-negative-mid edge-negative-mid; }
    &.color-warning   { @apply text-warning-mid edge-warning-mid; }
    &.color-accent    { @apply text-accent-mid edge-accent-mid; }
    &.color-invert    { @apply ink-invert-strong edge-invert-base; }
    &.color-disabled  { @apply ink-disabled edge-secondary-faint; }
  }

  &.shape-square { @apply rounded-sm; }
  &.shape-rounded { @apply rounded-full; }
}
</style>
